import { CSSProperties, HTMLAttributes, memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { getEventCellColorsFromHex } from "../../utils";
import { cn } from "@tellescope/ui";
import { addMinutes } from "date-fns/addMinutes";
import { EventWithLayout } from "../../types";
import { useDraggable } from "@dnd-kit/core";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { usePermissionQuery } from "@tellescope/tanstack-query";

export interface DayColumnEventCellProps {
  event: EventWithLayout;
  onClick?: (() => void) | { to: string; replace?: boolean };
  className?: string;
  style?: HTMLAttributes<HTMLDivElement>["style"];
  timezone?: string;
  draggable?: boolean;
  resizable?: boolean;
  durationOverride?: number;
  isDragGhost?: boolean;
}

const DayColumnEventCellInner = ({
  event,
  onClick,
  className,
  style,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  draggable: providedDraggable = false,
  resizable: providedResizable = false,
  durationOverride,
  isDragGhost = false,
}: DayColumnEventCellProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const eventStartDate = new Date(event.startTimeInMS);
  const baseDuration = event.actualDuration ?? event.durationInMinutes;
  const durationToUse = durationOverride ?? baseDuration;
  const isCompact = durationToUse < 15;
  const eventEndDate = addMinutes(eventStartDate, durationToUse);
  const { update } = usePermissionQuery({
    model: "calendar_events",
    record: event,
    actions: ["update"],
  });
  const draggable = providedDraggable && update.access;
  const resizable = providedResizable && update.access;

  const { resolvedColor, opacity, strikethrough } = useMemo(() => {
    if (event.cancelledAt || event.rescheduledAt) {
      return { resolvedColor: "#000000", opacity: 0.5, strikethrough: true };
    }

    return { resolvedColor: event.color };
  }, [event.cancelledAt, event.color, event.rescheduledAt]);

  const { backgroundColor, borderColor, textColor } = getEventCellColorsFromHex(resolvedColor);

  const moveDraggable = useDraggable({
    id: `day-column-event-move-${event.id}`,
    data: { type: "move", event },
    disabled: !draggable,
  });
  const resizeDraggable = useDraggable({
    id: `day-column-event-resize-${event.id}`,
    data: { type: "resize", event },
    disabled: !resizable,
  });

  const isAnyDragging = moveDraggable.isDragging || resizeDraggable.isDragging;
  const Component = onClick ? (typeof onClick === "function" ? "button" : Link) : "div";

  const moveTransform = moveDraggable.transform;
  const computedStyle: CSSProperties = {
    ...style,
    backgroundColor,
    borderColor,
    color: textColor,
    opacity: isAnyDragging ? 0.5 : opacity,
    paddingTop: isCompact ? Math.max(durationToUse / 15 - 1, 0) : 4,
    transform: moveTransform ? `translate3d(${moveTransform.x}px, ${moveTransform.y}px, 0)` : undefined,
    touchAction: draggable ? "none" : undefined,
    cursor: draggable && !isDragGhost ? (moveDraggable.isDragging ? "grabbing" : "grab") : undefined,
  };

  const setCellRef = (node: HTMLElement | null) => {
    if (draggable) moveDraggable.setNodeRef(node);
  };

  return (
    <Component
      ref={setCellRef}
      data-testid={`calendar-event-${event.id}`}
      style={computedStyle}
      className={cn(
        "border rounded-sm z-10 px-2 text-xs flex flex-col overflow-hidden relative",
        strikethrough && "line-through",
        durationToUse < 15 && "text-[10px]",
        className
      )}
      to={typeof onClick === "object" && typeof Component !== "string" ? onClick.to : ""}
      replace={typeof onClick === "object" ? onClick.replace : undefined}
      onClick={(e) => {
        if (onClick !== undefined) e.stopPropagation();
        if (typeof onClick === "function") onClick();
      }}
      {...(draggable ? moveDraggable.listeners : {})}
      {...(draggable ? moveDraggable.attributes : {})}
    >
      <span className="font-semibold text-left text-nowrap truncate min-h-min">
        {event.displayTitle || event.title}
      </span>
      <span className="text-nowrap truncate min-h-min">
        {formatInTimeZone(eventStartDate, timezone, "h:mm a")}
        {" - "}
        {formatInTimeZone(eventEndDate, timezone, "h:mm a")}
      </span>
      {resizable && !isDragGhost && (
        <div
          ref={resizeDraggable.setNodeRef}
          data-testid={`calendar-event-resize-${event.id}`}
          {...resizeDraggable.listeners}
          {...resizeDraggable.attributes}
          className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize"
          style={{ touchAction: "none" }}
        />
      )}
    </Component>
  );
};

export const DayColumnEventCell = memo(DayColumnEventCellInner);
